import { ProductReview } from 'types';
import StarRating from '@root/src/components/StarRating';
import Line from '@root/src/components/Line';
import Link from 'next/link';
import Loading from '@root/src/components/Loading';
import Reviews from '@root/src/components/Reviews';
import { Button as ButtonProps } from '@root/sanity/sanity.types';
import useSiteReviews from '@root/src/hooks/useSiteReviews';

const AWARDS_IMAGES = [
  {
    src: 'https://gentux.imgix.net/1594325408_weddingwirecoupleschoiceawards2019.png?fm=JPEG&bg=f8f8f8&w=58&auto=format',
    alt: 'Generation Tux WeddingWire Winner 2019',
  },
  {
    src: 'https://gentux.imgix.net/1535733019_wedding-wire-1100.png?fm=JPEG&bg=f8f8f8&w=58&auto=format',
    alt: 'Wedding Wire 1100+ reviews.',
  },
  {
    src: 'https://gentux.imgix.net/1606858008_theKnotBestofWeddings2021500x500.png?fm=JPEG&bg=f8f8f8&w=58&auto=format',
    alt: 'The Knot Best of Weddings Award',
  },
];

type FeaturedReviewsProps = {
  title: string;
  featuredReviewItems?: JSX.Element;
  showDynamicReviews: boolean;
  cta: ButtonProps;
};

const FeaturedReviews = ({
  title = 'Customer Reviews',
  featuredReviewItems,
  showDynamicReviews = false,
  cta,
  ...props
}: FeaturedReviewsProps) => {
  const [siteReviewsState, loadMoreReviews] = useSiteReviews();
  const { reviews, loading, averageStarRating, totalReviewCount, canLoadMore } = siteReviewsState;
  const formatReviews = (reviews: ProductReview[]) => {
    return reviews.map((review) => ({
      ...review,
      content: review.content.replace(/&#x27;/g, "'"),
      createdAt: new Date(review.createdAt).getTime(),
    }));
  };

  const renderDynamicReviews = () => {
    if (loading) return <Loading />;
    if (!loading && reviews.length > 0) {
      return <Reviews reviews={formatReviews(reviews)} canLoadMore={canLoadMore} loadMoreClicked={loadMoreReviews} />;
    }
    return null; // Simplified the no-reviews condition
  };

  return (
    <div className="m-section-hero container">
      <div className="grid grid-cols-12 sm:gap-x-64">
        <div className="col-span-12 mb-64 sm:col-span-4 md:mb-0 lg:col-span-3">
          <div className="mb-32 space-y-32 bg-white p-32">
            <h1 className="text-h1-display">{title}</h1>
            {!loading && (
              <div className="space-y-8">
                <h2 className="text-h4">Overall Rating</h2>

                <StarRating rating={averageStarRating} />

                <p className="text-sm">({totalReviewCount} reviews)</p>
              </div>
            )}

            <Line />
            {cta?.href && (
              <Link href={cta.href}>
                <a className={`btn w-full ${cta.style} ${cta.trackerClass ?? ''}`}>{cta.text}</a>
              </Link>
            )}
          </div>

          <div className="flex justify-around">
            {AWARDS_IMAGES.map((img, index) => (
              <img key={index} src={img.src} alt={img.alt} />
            ))}
          </div>
        </div>

        <div className="col-span-12 sm:col-span-8 lg:col-span-8 lg:col-start-5">
          {featuredReviewItems}

          {showDynamicReviews && renderDynamicReviews()}
        </div>
      </div>
    </div>
  );
};

export default FeaturedReviews;
